import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { LegendList } from '@legendapp/list';
import { DateTime } from 'luxon';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useFilter } from '@/data/store/useFilterStore';
import useTablePreferencesStore from '@/data/store/useTablePreferencesStore';
import useTableSelectionStore from '@/data/store/useTableSelectionStore';
import usePeopleBatchActions from '@/utils/hooks/usePeopleBatchActions';
import { Table } from '@/components/General/Table';
import Avatar from '@/components/People/Avatar';
import DirectoryPersonCard from '@/components/People/DirectoryPersonCard';
import PeopleAdvancedFilter from '@/components/Filters/PeopleAdvancedFilter';
import SearchBox from '@/components/General/inputs/SearchBox';
import useGuide from '@/utils/hooks/useGuide';
import GuideTarget from '@/components/Guides/GuideTarget';
import Checkbox from '@/components/General/inputs/Checkbox';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import ExportFieldsModal from '@/components/General/ExportFieldsModal';
import IconButton from '@/components/General/IconButton';
import Unicon from '@/components/General/Unicon';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import PeopleColumnSettingsModal from '@/components/People/PeopleColumnSettingsModal';
import usePlatform from '@/utils/hooks/usePlatform';
import { showAlert } from '@/utils/showAlert';
import Toast from 'react-native-toast-message';
import { archivePerson, exportPeopleData, getAllPeopleForSelection, getAllPeopleIdsForSelection, getPeopleByIds, getReachUserIdsAmong } from '@/data/people';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg, sanitizeFiltersForPlan, FREE_SORT_DENIED_KEYS } from '@/utils/organizationEntitlements';

const DIRECTORY_ID = 'people-directory';
const ADDRESS_COLUMN_IDS = ['street', 'streetLine2', 'city', 'state', 'zip'];
const DEFAULT_COLUMN_IDS = ['name', 'gender', 'dateOfBirth', 'phone', 'email', ...ADDRESS_COLUMN_IDS];
const SORT_OPTIONS = [
    { key: 'name', label: 'Name', direction: 'asc' },
    { key: 'mostRecentVisit', label: 'Last visit', direction: 'desc' },
    { key: 'createdAt', label: 'Recently added', direction: 'desc' },
];

const hasValue = value => value !== null && value !== undefined && value !== '';

const parseDate = value => {
    const normalized = Array.isArray(value) ? value[0] : value;
    if (!hasValue(normalized)) return null;
    if (normalized instanceof Date) return DateTime.fromJSDate(normalized);
    if (typeof normalized !== 'string') return null;
    const localDate = DateTime.fromFormat(normalized, 'MM/dd/yyyy');
    if (localDate.isValid) return localDate;
    const sqlDate = DateTime.fromSQL(normalized);
    if (sqlDate.isValid) return sqlDate;
    const isoDate = DateTime.fromISO(normalized);
    return isoDate.isValid ? isoDate : null;
};

const formatDate = (value, format = DateTime.DATE_MED) => parseDate(value)?.toLocaleString(format) || '';

const formatCustomFieldValue = (value, field) => {
    if (!hasValue(value)) return '';
    if (field.type === 'Checkbox' || typeof value === 'boolean') return value === true || value === 'true' ? 'Yes' : 'No';
    if (field.type === 'Date') return formatDate(value);
    if (field.type === 'Datetime') return formatDate(value, DateTime.DATETIME_MED);
    if (Array.isArray(value)) {
        return value.map(item => {
            const option = field.options?.find(candidate => (typeof candidate === 'object' ? candidate.value : candidate) === item);
            return option ? (typeof option === 'object' ? option.label : option) : item;
        }).join(', ');
    }
    return String(value);
};

export default function PeopleScreen() {
    const { theme: { colors, scheme } } = useUnistyles();
    const { isWeb } = usePlatform();
    const insets = useSafeAreaInsets();
    const router = useRouter();
    const { compose, templateID } = useLocalSearchParams();
    const campaignChannel = compose === 'sms' || compose === 'email' ? compose : null;
    const campaignTemplateID = campaignChannel === 'email' && typeof templateID === 'string' ? templateID : null;
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const filterState = useFilter('people');
    const listRef = useRef();
    const filterRef = useRef(null);
    const seenPeopleMap = useRef(new Map());
    const [searchText, setSearchText] = useState('');
    const [includeArchived, setIncludeArchived] = useState(false);
    const [sortConfig, setSortConfig] = useState(null);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
    const [selectedPeopleForExport, setSelectedPeopleForExport] = useState(null);
    const [isSelecting, setIsSelecting] = useState(false);
    const storedColumnIds = useTablePreferencesStore(state => state.tables[DIRECTORY_ID]?.columnIds);
    const storedViewMode = useTablePreferencesStore(state => state.tables[DIRECTORY_ID]?.viewMode);
    const hydratePreferences = useTablePreferencesStore(state => state.hydrate);
    const setStoredColumnIds = useTablePreferencesStore(state => state.setColumnIds);
    const setStoredViewMode = useTablePreferencesStore(state => state.setViewMode);
    const viewMode = isWeb ? 'table' : (storedViewMode || 'cards');
    const tableState = useTableSelectionStore(state => state.tables[DIRECTORY_ID]);
    const toggleItem = useTableSelectionStore(state => state.toggleItem);
    const handleSelectAll = useTableSelectionStore(state => state.handleSelectAll);
    const clearSelection = useTableSelectionStore(state => state.clearSelection);
    const getSelectionCount = useTableSelectionStore(state => state.getSelectionCount);
    const selectionMode = tableState?.selectionMode || 'include';
    const selectedIds = tableState?.selectedIds || new Set();
    const deselectedIds = tableState?.deselectedIds || new Set();

    useEffect(() => {
        if (campaignChannel) {
            clearSelection(DIRECTORY_ID);
            setIsSelecting(true);
        }
    }, [campaignChannel, clearSelection]);

    useEffect(() => {
        hydratePreferences();
    }, [hydratePreferences]);

    const { entitlements } = useOrganizationEntitlements();
    const isFree = isFreeOrg(entitlements);

    // Strip paid-only filter conditions (custom fields, attendance, roles)
    // from any persisted filter state before it reaches the RPC — the backend
    // rejects them outright on Free with 42501.
    const combinedFilters = useMemo(() => ({
        ...sanitizeFiltersForPlan(filterState.peopleFilters, entitlements),
        includeArchived: includeArchived ? 'true' : 'false',
    }), [filterState.peopleFilters, includeArchived, entitlements]);

    const { data: filteredPeople, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } = Query.people.all({
        searchTerm: searchText,
        filters: combinedFilters,
        filterLogic: filterState.filterLogic,
        sortKey: sortConfig?.key,
        sortDirection: sortConfig?.direction,
    });
    const { data: loggedInUser } = Query.people.loggedInUser();
    const canViewBasic = loggedInUser?.isAdmin || loggedInUser?.can?.('View Basic Info');
    const canViewContact = loggedInUser?.isAdmin || loggedInUser?.can?.('View Contact Info');
    const canViewCustom = !isFree && (loggedInUser?.isAdmin || loggedInUser?.can?.('View Custom Fields'));
    const { data: customFields = [] } = Query.personCustomFields.all({ options: { enabled: !!canViewCustom } });
    const flatData = useMemo(() => filteredPeople?.pages.flatMap(page => page.data) || [], [filteredPeople]);
    const guide = useGuide('people.directory', { ready: !isFetching, disabled: !!campaignChannel, extra: { isWeb, viewMode, hasPeople: flatData.length > 0 } });
    const totalCount = filteredPeople?.pages?.[0]?.count || 0;
    const selectedCount = getSelectionCount(DIRECTORY_ID, totalCount);
    const isAllSelected = selectionMode === 'exclude' && deselectedIds.size === 0;
    const selectionActive = isSelecting || selectedCount > 0;
    const planSortOptions = isFree ? SORT_OPTIONS.filter(o => !FREE_SORT_DENIED_KEYS.includes(o.key)) : SORT_OPTIONS;
    const availableSortOptions = canViewBasic ? planSortOptions : planSortOptions.slice(0, 1);
    const activeSort = availableSortOptions.find(option => option.key === sortConfig?.key);

    useEffect(() => {
        seenPeopleMap.current.clear();
        flatData.forEach(person => person?.id && seenPeopleMap.current.set(person.id, person));
    }, [combinedFilters, filterState.filterLogic, searchText, sortConfig]);

    useEffect(() => {
        flatData.forEach(person => person?.id && seenPeopleMap.current.set(person.id, person));
    }, [flatData]);

    useEffect(() => {
        navigation.setOptions({
            headerRight: campaignChannel ? null : () => (
                <GuideTarget id="people.actions">
                <DropdownMenu
                    testID="PeopleDirectoryMenu"
                    options={[
                        ...(loggedInUser?.can?.('Create People') ? [{ name: 'Add New Person', onSelect: () => router.push('/people/create'), icon: dropdownIcons.create }] : []),
                        ...(loggedInUser?.isAdmin || loggedInUser?.can?.('Edit People') ? [{ name: 'Merge People', icon: { android: 'git_merge', ios: 'arrow.merge', web: 'git-merge-outline' }, onSelect: () => router.push('/settings/organization/merge-people') }] : []),
                        ...(loggedInUser?.can?.('Manage Profile Views') ? [{ name: 'Profile Views', icon: { android: 'badge', ios: 'person.text.rectangle', web: 'id-card-outline' }, onSelect: () => router.push('/settings/organization/profile-views') }] : []),
                        { name: 'Export to CSV', icon: dropdownIcons.export, onSelect: () => setExportModalOpen(true) },
                        { name: includeArchived ? 'Hide Archived' : 'Show Archived', icon: dropdownIcons.archive, onSelect: () => setIncludeArchived(value => !value) },
                        { name: 'Tour this page', icon: dropdownIcons.help, onSelect: () => guide?.replay('people.directory') },
                    ]}
                />
                </GuideTarget>
            ),
            headerSearchBarOptions: null,
        });
    }, [campaignChannel, includeArchived, navigation, router, scheme, loggedInUser, guide]);

    const refreshList = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: QueryKeys.people.all({
                searchTerm: searchText,
                filters: combinedFilters,
                filterLogic: filterState.filterLogic,
                sortKey: sortConfig?.key,
                sortDirection: sortConfig?.direction,
            }),
        });
    }, [combinedFilters, filterState.filterLogic, queryClient, searchText, sortConfig]);

    const getSelectedPeopleIds = async (mode, ids) => {
        if (mode === 'include') return Array.from(ids);
        return getAllPeopleIdsForSelection({
            searchTerm: searchText,
            filters: combinedFilters,
            filterLogic: filterState.filterLogic,
            excludeIds: Array.from(ids),
        });
    };

    const getSelectedPeople = async (mode, ids) => {
        if (mode === 'include') {
            const cached = [];
            const missingIds = [];
            Array.from(ids).filter(Boolean).forEach(id => {
                const person = seenPeopleMap.current.get(id);
                if (person) cached.push(person);
                else missingIds.push(id);
            });
            const fetched = missingIds.length ? await getPeopleByIds(missingIds) : [];
            return [...cached, ...fetched];
        }
        return getAllPeopleForSelection({
            searchTerm: searchText,
            filters: combinedFilters,
            filterLogic: filterState.filterLogic,
            excludeIds: Array.from(ids),
            returnFields: ['id', 'firstName', 'lastName', 'email', 'Phones', 'Address', 'avatarUpdatedAt', 'avatarStorageKey'],
        });
    };

    const { peopleBatchActions: sharedPeopleActions, modalElements } = usePeopleBatchActions({
        getSelectedPersonIds: getSelectedPeopleIds,
        getSelectedPeople,
    });

    const peopleBatchActions = [
        {
            name: 'Export Selected',
            icon: dropdownIcons.export,
            onSelect: async (mode, ids) => {
                const selected = await getSelectedPeople(mode, ids);
                setSelectedPeopleForExport(selected.length ? selected : null);
                setExportModalOpen(true);
            },
        },
        ...(loggedInUser?.can?.('Edit People') ? [{
            name: 'Edit People',
            icon: dropdownIcons.edit,
            onSelect: async (mode, ids) => {
                const idsToEdit = await getSelectedPeopleIds(mode, ids);
                if (!idsToEdit.length) {
                    Toast.show({ type: 'error', text2: 'No people found for selected items.' });
                    return;
                }
                router.push({ pathname: '/people/create', params: { batchIds: JSON.stringify(idsToEdit) } });
            },
        }] : []),
        ...sharedPeopleActions,
        {
            name: 'Archive',
            icon: dropdownIcons.archive,
            destructive: true,
            onSelect: async (mode, ids, count) => {
                const idsToArchive = await getSelectedPeopleIds(mode, ids);
                showAlert({
                    title: `Archive ${count} ${count === 1 ? 'Person' : 'People'}?`,
                    subtitle: `This will archive ${count === 1 ? 'this person' : 'these people'}. They will no longer appear in active lists.`,
                    confirmText: 'Archive',
                    cancelText: 'Cancel',
                    isDestructive: true,
                    onConfirm: async () => {
                        try {
                            const reachUserIds = await getReachUserIdsAmong(idsToArchive);
                            const archiveIds = idsToArchive.filter(id => !reachUserIds.includes(id));
                            const results = await Promise.allSettled(archiveIds.map(id => archivePerson(id)));
                            const archivedCount = results.filter(result => result.status === 'fulfilled').length;
                            const failedCount = results.filter(result => result.status === 'rejected').length;
                            queryClient.invalidateQueries({ queryKey: QueryKeys.people.all() });
                            if (archivedCount) Toast.show({ type: 'success', text2: `${archivedCount} ${archivedCount === 1 ? 'person' : 'people'} archived.` });
                            if (reachUserIds.length) Toast.show({
                                type: reachUserIds.length === 1 ? 'info' : 'error',
                                text1: `${reachUserIds.length} ${reachUserIds.length === 1 ? 'person was' : 'people were'} not archived.`,
                                text2: 'Reach users cannot be archived. If users are connected to the wrong profiles, a Reach administrator must change the connected profile.',
                                visibilityTime: 6000,
                            });
                            if (failedCount) Toast.show({ type: 'error', text2: `Failed to archive ${failedCount} ${failedCount === 1 ? 'person' : 'people'}. Please try again.` });
                        } catch {
                            Toast.show({ type: 'error', text2: 'Failed to archive some people. Please try again.' });
                        }
                    },
                });
            },
        },
    ];

    const visiblePeopleBatchActions = campaignChannel
        ? sharedPeopleActions.filter(action => action.name === (campaignChannel === 'sms' ? 'Send SMS' : 'Send Email'))
        : peopleBatchActions;

    const campaignBatchActions = campaignTemplateID
        ? visiblePeopleBatchActions.map(action => ({
            ...action,
            onSelect: async (mode, ids) => {
                const selectedIds = await getSelectedPeopleIds(mode, ids);
                if (!selectedIds.length) {
                    Toast.show({ type: 'error', text2: 'No people found for selected items.' });
                    return;
                }
                router.push({
                    pathname: '/emails/editor',
                    params: {
                        recipientIDs: JSON.stringify(selectedIds),
                        templateID: campaignTemplateID,
                    },
                });
            },
        }))
        : visiblePeopleBatchActions;

    const allColumnDefinitions = useMemo(() => {
        const cellText = { fontSize: 14, color: colors.textSecondary };
        const dateRenderer = accessor => person => <Text style={cellText}>{formatDate(person[accessor])}</Text>;
        const addressFieldRenderer = field => person => <Text style={cellText}>{person.Address?.[field] || ''}</Text>;
        return [
            {
                id: 'name',
                value: 'Name',
                width: 250,
                accessor: 'name',
                sortable: true,
                renderer: person => (
                    <View style={styles.tableNameCell}>
                        <Avatar size={34} profile={person} disableBorder />
                        <Text numberOfLines={1} style={styles.tableName(person.isArchived)}>{person.firstName} {person.lastName}</Text>
                    </View>
                ),
            },
            ...(canViewBasic ? [
                { id: 'gender', value: 'Gender', accessor: 'gender', sortable: true },
                { id: 'dateOfBirth', value: 'Date of Birth', accessor: 'dateOfBirth', sortable: true, sortType: 'date', renderer: dateRenderer('dateOfBirth') },
                { id: 'age', value: 'Age', accessor: 'age', sortable: true, sortType: 'number', renderer: person => {
                    const birthDate = person.dateOfBirth ? DateTime.fromISO(person.dateOfBirth) : null;
                    const age = birthDate?.isValid ? Math.floor(DateTime.now().diff(birthDate, 'years').years) : '';
                    return <Text style={cellText}>{age}</Text>;
                } },
                // Attendance columns are paid-only — filter_people strips them
                // for Free plans and rejects their sort keys.
                ...(isFree ? [] : [
                    { id: 'timesAttended', value: 'Times Attended', accessor: 'timesAttended', sortable: true, sortType: 'number' },
                    { id: 'mostRecentVisit', value: 'Last Visit', accessor: 'mostRecentVisit', sortable: true, sortType: 'date', renderer: dateRenderer('mostRecentVisit') },
                    { id: 'firstVisit', value: 'First Visit', accessor: 'firstVisit', sortable: true, sortType: 'date', renderer: dateRenderer('firstVisit') },
                ]),
                { id: 'hasAccount', value: 'Has Account', accessor: 'hasAccount', sortable: true, renderer: person => <Text style={cellText}>{hasValue(person.hasAccount) ? (person.hasAccount ? 'Yes' : 'No') : ''}</Text> },
                { id: 'createdAt', value: 'Date Added', accessor: 'createdAt', sortable: true, sortType: 'date', renderer: dateRenderer('createdAt') },
                { id: 'updatedAt', value: 'Last Updated', accessor: 'updatedAt', sortable: true, sortType: 'date', renderer: dateRenderer('updatedAt') },
            ] : []),
            ...(canViewContact ? [
                { id: 'email', value: 'Email', accessor: 'email', sortable: true },
                { id: 'phone', value: 'Phone', accessor: 'phone', sortable: true, renderer: person => person.Phones?.length ? person.Phones.map((phone, index) => <Text key={`${phone.phoneNumber}-${index}`} style={cellText}>{phone.phoneNumber}</Text>) : <Text style={cellText}></Text> },
                { id: 'street', value: 'Street', accessor: 'street', sortable: true, renderer: addressFieldRenderer('street') },
                { id: 'streetLine2', value: 'Street Line 2', accessor: 'streetLine2', sortable: true, renderer: addressFieldRenderer('streetLine2') },
                { id: 'city', value: 'City', accessor: 'city', sortable: true, renderer: addressFieldRenderer('city') },
                { id: 'state', value: 'State', accessor: 'state', sortable: true, renderer: addressFieldRenderer('state') },
                { id: 'zip', value: 'ZIP', accessor: 'zip', sortable: true, renderer: addressFieldRenderer('zip') },
            ] : []),
            ...(canViewCustom ? customFields.filter(field => field.type !== 'File Upload').map(field => ({
                id: `custom:${field.id}`,
                value: field.name,
                accessor: `custom:${field.id}`,
                sortable: true,
                sortType: field.type === 'Number' ? 'number' : (field.type === 'Date' || field.type === 'Datetime' ? 'date' : 'text'),
                section: field.Section?.name || 'Custom fields',
                renderer: person => <Text style={cellText}>{formatCustomFieldValue(person.customFieldValues?.[field.id], field)}</Text>,
            })) : []),
        ];
    }, [canViewBasic, canViewContact, canViewCustom, isFree, colors.textSecondary, customFields]);

    const selectedColumnIds = useMemo(() => {
        const availableIds = new Set(allColumnDefinitions.map(column => column.id));
        const storedIds = storedColumnIds?.length ? storedColumnIds : DEFAULT_COLUMN_IDS;
        const preferredIds = storedIds.flatMap(id => id === 'address' ? ADDRESS_COLUMN_IDS : [id]);
        return ['name', ...preferredIds.filter(id => id !== 'name' && availableIds.has(id))];
    }, [allColumnDefinitions, storedColumnIds]);

    const tableColumns = useMemo(() => {
        const columnMap = new Map(allColumnDefinitions.map(column => [column.id, column]));
        return selectedColumnIds.map(id => columnMap.get(id)).filter(Boolean);
    }, [allColumnDefinitions, selectedColumnIds]);

    const saveColumns = columnIds => {
        setStoredColumnIds(DIRECTORY_ID, columnIds);
        if (sortConfig && !columnIds.includes(sortConfig.key)) setSortConfig(null);
    };

    const runBatchAction = action => {
        action.onSelect(selectionMode, selectionMode === 'include' ? selectedIds : deselectedIds, selectedCount);
        clearSelection(DIRECTORY_ID);
        setIsSelecting(false);
    };

    const handlePersonLongPress = useCallback(person => {
        if (!selectionActive) setIsSelecting(true);
        toggleItem(DIRECTORY_ID, person.id);
    }, [selectionActive, toggleItem]);

    const renderPerson = useCallback(({ item, index }) => {
        const card = (
            <DirectoryPersonCard
                person={item}
                tableId={DIRECTORY_ID}
                selecting={selectionActive}
                canViewBasic={canViewBasic}
                canViewContact={canViewContact}
                onOpen={() => campaignChannel ? toggleItem(DIRECTORY_ID, item.id) : router.push(`/people/${item.id}`)}
                onToggle={() => toggleItem(DIRECTORY_ID, item.id)}
                onLongPress={() => handlePersonLongPress(item)}
            />
        );
        // First card doubles as the tour's "open a profile" target.
        return index === 0 ? (
            <GuideTarget id="people.person" activate={campaignChannel ? undefined : () => router.push(`/people/${item.id}`)}>{card}</GuideTarget>
        ) : card;
    }, [campaignChannel, canViewBasic, canViewContact, handlePersonLongPress, router, selectionActive, toggleItem]);

    const handleExport = selectedFields => exportPeopleData({
        ...(selectedPeopleForExport
            ? { people: selectedPeopleForExport }
            : { totalCount, searchTerm: searchText, filters: sanitizeFiltersForPlan(filterState.peopleFilters, entitlements), filterLogic: filterState.filterLogic }),
        selectedFields,
    });

    return (
        <View testID="PeopleDirectoryScreen" style={styles.screen}>
            <ExportFieldsModal
                isOpen={exportModalOpen}
                onClose={() => { setExportModalOpen(false); setSelectedPeopleForExport(null); }}
                onExport={handleExport}
                title="Export People"
            />
            <PeopleColumnSettingsModal
                isOpen={columnSettingsOpen}
                onClose={() => setColumnSettingsOpen(false)}
                columns={allColumnDefinitions}
                selectedColumnIds={selectedColumnIds}
                defaultColumnIds={DEFAULT_COLUMN_IDS.filter(id => allColumnDefinitions.some(column => column.id === id))}
                onSave={saveColumns}
                includeCards={false}
            />
            {modalElements}
            <View testID="PeopleDirectoryContent" style={styles.content}>
                {!!campaignChannel && <View testID="CampaignRecipientPrompt" style={styles.campaignPrompt}>
                    <Unicon name={campaignChannel === 'sms' ? 'chatbubble-outline' : 'mail-outline'} size={20} colorVariant="connect" />
                    <Text style={styles.campaignPromptText}>{campaignTemplateID ? 'Select recipients to send email...' : `Select people to send ${campaignChannel === 'sms' ? 'an SMS' : 'an email'} to, then tap Send ${campaignChannel === 'sms' ? 'SMS' : 'Email'}.`}</Text>
                    <Pressable accessibilityLabel="Cancel campaign" testID="CampaignRecipientCancel" onPress={() => router.back()}><Unicon name="close" size={20} colorVariant="textSecondary" /></Pressable>
                </View>}
                <View style={styles.header}>
                    <View style={styles.summaryRow}>
                        <View style={styles.summaryCopy}>
                            <Text style={styles.eyebrow}>CHURCH DIRECTORY</Text>
                            <Text style={styles.count}>{isFetching && !flatData.length ? 'Finding people…' : `${totalCount.toLocaleString()} ${totalCount === 1 ? 'person' : 'people'}`}</Text>
                        </View>
                        {viewMode === 'cards' && (
                            <GuideTarget id="people.selection" activate={() => {
                                if (selectionActive) clearSelection(DIRECTORY_ID);
                                setIsSelecting(value => !value);
                            }}>
                            <Pressable
                                testID="PeopleDirectorySelect"
                                onPress={() => {
                                    if (selectionActive) clearSelection(DIRECTORY_ID);
                                    setIsSelecting(value => !value);
                                }}
                                style={styles.selectButton(selectionActive)}
                            >
                                <Text style={styles.selectButtonText(selectionActive)}>{selectionActive ? 'Cancel' : 'Select'}</Text>
                            </Pressable>
                            </GuideTarget>
                        )}
                    </View>
                    <View style={styles.searchRow}>
                        <GuideTarget id="people.search" style={styles.searchBox}>
                            <SearchBox
                                testID="PeopleDirectory"
                                searchText={searchText}
                                placeholder="Search by name, email, or phone"
                                textChangeDelay={250}
                                onChangeText={setSearchText}
                            />
                        </GuideTarget>
                        <GuideTarget id="people.filters" activate={() => filterRef.current?.open()}><PeopleAdvancedFilter ref={filterRef} filterId="people" guideNamespace="people" /></GuideTarget>
                        {viewMode === 'table' && (
                            <GuideTarget id="people.columns" activate={() => setColumnSettingsOpen(true)}>
                            <IconButton
                                iconName="settings-outline"
                                iconSize={21}
                                type="textSecondary"
                                tooltipText="Choose directory columns"
                                testID="PeopleColumnSettings"
                                onPress={() => setColumnSettingsOpen(true)}
                            />
                            </GuideTarget>
                        )}
                        {isWeb && (
                            <IconButton
                                iconName="refresh"
                                iconSize={21}
                                type="textSecondary"
                                disabled={isFetching}
                                tooltipText="Refresh people"
                                testID="PeopleDirectoryRefresh"
                                onPress={refreshList}
                            />
                        )}
                    </View>
                    {!isWeb && (
                        <View style={styles.toolRow(isWeb)}>
                            <GuideTarget id="people.view" style={styles.viewToggle}>
                                <Pressable testID="PeopleDirectoryCardView" accessibilityLabel="Card view" onPress={() => setStoredViewMode(DIRECTORY_ID, 'cards')} style={styles.viewButton(viewMode === 'cards')}>
                                    <Unicon name="people-outline" size={17} colorVariant={viewMode === 'cards' ? 'people' : 'textSecondary'} />
                                    <Text style={styles.viewLabel(viewMode === 'cards')}>Directory</Text>
                                </Pressable>
                                <Pressable testID="PeopleDirectoryTableView" accessibilityLabel="Table view" onPress={() => setStoredViewMode(DIRECTORY_ID, 'table')} style={styles.viewButton(viewMode === 'table')}>
                                    <Unicon name="list-outline" size={18} colorVariant={viewMode === 'table' ? 'people' : 'textSecondary'} />
                                    <Text style={styles.viewLabel(viewMode === 'table')}>Table</Text>
                                </Pressable>
                            </GuideTarget>
                            <View style={styles.sortControls}>
                            {viewMode === 'cards' && (
                                <>
                                    <DropdownMenu
                                        testID="PeopleDirectorySort"
                                        title="Sort people"
                                        guideTargetID="people.sort"
                                        options={availableSortOptions.map(option => ({
                                            name: option.label,
                                            selected: option.key === sortConfig?.key,
                                            onSelect: () => setSortConfig({ key: option.key, direction: option.key === sortConfig?.key ? sortConfig.direction : option.direction }),
                                        }))}
                                        triggerComponent={(
                                            <View style={styles.sortTrigger}>
                                                <Unicon name="swap-vertical-outline" size={16} colorVariant="textSecondary" />
                                                <Text style={styles.sortText}>{activeSort?.label || 'Sort'}</Text>
                                            </View>
                                        )}
                                    />
                                    {sortConfig && (
                                        <Pressable testID="PeopleDirectorySortDirection" accessibilityLabel="Change sort direction" onPress={() => setSortConfig(config => ({ ...config, direction: config.direction === 'asc' ? 'desc' : 'asc' }))} style={styles.directionButton}>
                                            <Unicon name={sortConfig.direction === 'asc' ? 'arrow-up' : 'arrow-down'} size={16} colorVariant="people" />
                                        </Pressable>
                                    )}
                                </>
                            )}
                            </View>
                        </View>
                    )}
                    {includeArchived && (
                        <View style={styles.archivedNotice}>
                            <Unicon name="archive-outline" size={14} colorVariant="people" />
                            <Text style={styles.archivedNoticeText}>Archived profiles included</Text>
                        </View>
                    )}
                    {viewMode === 'cards' && selectionActive && (
                        <View testID="PeopleDirectorySelectionBar" style={styles.selectionBar}>
                            <Checkbox
                                testID="PeopleDirectorySelectAll"
                                checked={isAllSelected}
                                indeterminate={!isAllSelected && selectedCount > 0}
                                onChange={() => handleSelectAll(DIRECTORY_ID)}
                                size={20}
                            />
                            <Text style={styles.selectionText}>{selectedCount ? `${isAllSelected ? 'All' : selectedCount} selected` : 'Select people'}</Text>
                            <Pressable onPress={() => { clearSelection(DIRECTORY_ID); setIsSelecting(false); }} style={styles.clearButton}>
                                <Text style={styles.clearText}>Clear</Text>
                            </Pressable>
                            {campaignChannel ? (
                                <Pressable testID="CampaignSendSelected" accessibilityLabel={`Send ${campaignChannel === 'sms' ? 'SMS' : 'Email'} to selected people`} disabled={!selectedCount || !campaignBatchActions.length} onPress={() => runBatchAction(campaignBatchActions[0])} style={styles.batchButton(!selectedCount)}>
                                    <Text style={styles.batchButtonText}>Send {campaignChannel === 'sms' ? 'SMS' : 'Email'}</Text>
                                </Pressable>
                            ) : <DropdownMenu
                                testID="BatchActions"
                                disabled={!selectedCount}
                                options={visiblePeopleBatchActions.map(action => ({ ...action, onSelect: () => runBatchAction(action) }))}
                                triggerComponent={(
                                    <View style={styles.batchButton(!selectedCount)}>
                                        <Text style={styles.batchButtonText}>Actions</Text>
                                        <Unicon name="chevron-down" size={14} colorVariant="onPeople" />
                                    </View>
                                )}
                            />}
                        </View>
                    )}
                </View>
                <View style={styles.listArea}>
                    {viewMode === 'cards' ? (
                        <LegendList
                            ref={listRef}
                            data={flatData}
                            renderItem={renderPerson}
                            extraData={[selectionActive, canViewBasic, canViewContact]}
                            keyExtractor={item => item.id}
                            estimatedItemSize={100}
                            contentInsetAdjustmentBehavior="automatic"
                            contentContainerStyle={[styles.listContent, { paddingBottom: 24 + insets.bottom }]}
                            showsVerticalScrollIndicator={false}
                            keyboardShouldPersistTaps="handled"
                            refreshing={false}
                            onRefresh={refreshList}
                            onEndReachedThreshold={0.5}
                            onEndReached={() => {
                                if (!isFetching && hasNextPage) fetchNextPage();
                            }}
                            ListEmptyComponent={!isFetching ? <EmptyListPlaceholder spacing={20} size={100} message="No people match this view" /> : null}
                            ListFooterComponent={isFetching && (
                                <View testID="PeopleDirectoryLoading" style={styles.loadingFooter}>
                                    <ActivityIndicator size="small" color={colors.people} />
                                </View>
                            )}
                        />
                    ) : (
                        <Table
                            guideSelectionTarget="people.selection"
                            guideSortTarget="people.sort"
                            guideRowTarget="people.person"
                            tableId={DIRECTORY_ID}
                            columns={tableColumns}
                            data={flatData}
                            totalCount={totalCount}
                            sortConfig={sortConfig}
                            onSortChange={setSortConfig}
                            onRowClick={person => campaignChannel ? toggleItem(DIRECTORY_ID, person.id) : router.push(`/people/${person.id}`)}
                            isLoading={isFetching || isFetchingNextPage}
                            batchSelection
                            batchActions={campaignBatchActions}
                            singleBatchAction={!!campaignChannel}
                            estimatedItemSize={60}
                            contentInsetAdjustmentBehavior="automatic"
                            showsVerticalScrollIndicator={false}
                            keyboardShouldPersistTaps="handled"
                            onRefresh={refreshList}
                            refreshing={false}
                            onEndReachedThreshold={0.5}
                            onEndReached={() => {
                                if (!isFetching && hasNextPage) fetchNextPage();
                            }}
                        />
                    )}
                </View>
            </View>
        </View>
    );
}

const styles = StyleSheet.create(theme => ({
    screen: {
        flex: 1,
        width: '100%',
        backgroundColor: 'transparent',
    },
    content: {
        flex: 1,
        width: '100%',
    },
    campaignPrompt: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingHorizontal: theme.spacing.md,
        paddingVertical: 12,
        backgroundColor: theme.colors.connectContainer,
    },
    campaignPromptText: {
        flex: 1,
        color: theme.colors.text,
        fontFamily: theme.fonts.heading,
        fontSize: 13,
        lineHeight: 18,
    },
    header: {
        paddingHorizontal: theme.spacing.md,
        paddingTop: 14,
        paddingBottom: 10,
        gap: 12,
        zIndex: 2,
    },
    summaryRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing.md,
    },
    summaryCopy: {
        gap: 3,
    },
    eyebrow: {
        color: theme.colors.people,
        fontFamily: theme.fonts.bold,
        fontSize: 10,
        letterSpacing: 1.5,
    },
    count: {
        color: theme.colors.text,
        fontFamily: theme.fonts.heading,
        fontSize: 18,
        letterSpacing: -0.25,
        fontVariant: ['tabular-nums'],
    },
    selectButton: active => ({
        paddingHorizontal: 14,
        paddingVertical: 7,
        borderRadius: 999,
        backgroundColor: active ? theme.colors.peopleContainer : theme.colors.surface,
        boxShadow: theme.shadows.card,
    }),
    selectButtonText: active => ({
        color: active ? theme.colors.people : theme.colors.textSecondary,
        fontFamily: theme.fonts.bold,
        fontSize: 13,
    }),
    searchRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    searchBox: {
        flex: 1,
        minWidth: 0,
    },
    toolRow: isWeb => ({
        flexDirection: 'row',
        justifyContent: isWeb ? 'flex-end' : 'space-between',
        alignItems: 'center',
        gap: 10,
    }),
    viewToggle: {
        flexDirection: 'row',
        padding: 3,
        borderRadius: theme.roundness.md,
        backgroundColor: theme.colors.surfaceVariant,
    },
    viewButton: active => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 11,
        paddingVertical: 7,
        borderRadius: 9,
        backgroundColor: active ? theme.colors.surface : 'transparent',
        boxShadow: active ? theme.shadows.card : theme.shadows.none,
    }),
    viewLabel: active => ({
        color: active ? theme.colors.people : theme.colors.textSecondary,
        fontFamily: active ? theme.fonts.bold : theme.fonts.body,
        fontSize: 12,
    }),
    sortControls: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    sortTrigger: {
        minHeight: 36,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 10,
        borderRadius: 10,
        backgroundColor: theme.colors.surface,
        boxShadow: theme.shadows.card,
    },
    sortText: {
        color: theme.colors.textSecondary,
        fontFamily: theme.fonts.body,
        fontSize: 12,
    },
    directionButton: {
        width: 36,
        height: 36,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 10,
        backgroundColor: theme.colors.peopleContainer,
    },
    archivedNotice: {
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        paddingHorizontal: 9,
        paddingVertical: 5,
        borderRadius: 999,
        backgroundColor: theme.colors.peopleContainer,
    },
    archivedNoticeText: {
        color: theme.colors.people,
        fontFamily: theme.fonts.bold,
        fontSize: 11,
    },
    selectionBar: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        padding: 10,
        borderRadius: theme.roundness.md,
        backgroundColor: theme.colors.peopleContainer,
    },
    selectionText: {
        flex: 1,
        color: theme.colors.people,
        fontFamily: theme.fonts.bold,
        fontSize: 13,
        fontVariant: ['tabular-nums'],
    },
    clearButton: {
        paddingHorizontal: 5,
        paddingVertical: 6,
    },
    clearText: {
        color: theme.colors.people,
        fontFamily: theme.fonts.bold,
        fontSize: 12,
    },
    batchButton: disabled => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        paddingHorizontal: 11,
        paddingVertical: 8,
        borderRadius: 9,
        backgroundColor: theme.colors.people,
        opacity: disabled ? 0.45 : 1,
    }),
    batchButtonText: {
        color: theme.colors.onPeople,
        fontFamily: theme.fonts.bold,
        fontSize: 12,
    },
    listArea: {
        flex: 1,
        minHeight: 0,
    },
    listContent: {
        gap: 10,
        paddingTop: 4,
    },
    loadingFooter: {
        minHeight: 70,
        alignItems: 'center',
        justifyContent: 'center',
    },
    tableNameCell: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    tableName: archived => ({
        flex: 1,
        color: archived ? theme.colors.textTertiary : theme.colors.text,
        fontFamily: theme.fonts.heading,
        fontSize: 14,
    }),
    tableText: {
        color: theme.colors.textSecondary,
        fontFamily: theme.fonts.body,
        fontSize: 13,
    },
}));
